import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { Select, TextInput } from '../../components/Field';
import { DataTable } from '../../components/Table';
import type { Column } from '../../components/Table';
import { Modal } from '../../overlay/Modal';
import { useOverlay } from '../../overlay/context';
import { toast } from '../../components/Toast';
import { useAuthStore } from '../../app/authStore';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime, formatRelativeTime } from '@shared/format';
import type { AuditLog, AuditResult, Paginated } from '@shared/types';

const PAGE_SIZE = 30;

const RESULT_LABELS: Record<AuditResult, string> = {
  success: '成功',
  denied: '被拒绝',
  failure: '失败',
};

const TARGET_TYPES = ['drone', 'user', 'group', 'settings', 'auth', 'system'] as const;

const TARGET_LABELS: Record<string, string> = {
  drone: '无人机',
  user: '用户',
  group: '分组',
  settings: '平台设置',
  auth: '认证',
  system: '系统',
};

/** 审计日志：所有授权判定与高风险操作都会落到这里。 */
export function AuditPage() {
  const overlay = useOverlay();
  const permissions = useAuthStore((state) => state.permissions);
  const canPrune = permissions.includes('settings.manage');

  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [result, setResult] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Paginated<AuditLog> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AuditLog | null>(null);
  const [pruning, setPruning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result2 = await api.get<Paginated<AuditLog>>('/api/audit', {
        query: {
          page,
          pageSize: PAGE_SIZE,
          action,
          targetType,
          result,
          from: from === '' ? undefined : new Date(`${from}T00:00:00`).toISOString(),
          to: to === '' ? undefined : new Date(`${to}T23:59:59`).toISOString(),
        },
      });
      setData(result2);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, [page, action, targetType, result, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [action, targetType, result, from, to]);

  const rows = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  async function prune(trigger: HTMLElement | null) {
    const confirmed = await overlay.confirm({
      placementKey: 'audit:prune',
      trigger,
      title: '清理过期审计日志',
      description: '将按平台设置中配置的保留天数删除更早的日志，该操作不可撤销。',
      confirmText: '开始清理',
      tone: 'danger',
    });
    if (!confirmed) return;
    setPruning(true);
    try {
      const removed = await api.post<{ removed: number; retentionDays: number }>('/api/audit/prune', {});
      toast.ok('清理完成', `已删除 ${removed.removed} 条超过 ${removed.retentionDays} 天的日志。`);
      void load();
    } catch (cause) {
      toast.critical('清理失败', errorMessage(cause));
    } finally {
      setPruning(false);
    }
  }

  const columns = useMemo<Column<AuditLog>[]>(
    () => [
      {
        key: 'at',
        header: '时间',
        width: '160px',
        render: (log) => (
          <span className="text-xs text-fg-muted" title={formatDateTime(log.at)}>
            {formatDateTime(log.at)}
          </span>
        ),
      },
      {
        key: 'actor',
        header: '操作人',
        render: (log) => (
          <span className="font-mono text-xs text-fg">{log.actorEmail || '—'}</span>
        ),
      },
      {
        key: 'action',
        header: '动作',
        render: (log) => <span className="font-mono text-xs text-fg">{log.action}</span>,
      },
      {
        key: 'target',
        header: '目标',
        hideOnMobile: true,
        render: (log) => (
          <span className="text-xs text-fg-muted">
            {log.targetType ? (TARGET_LABELS[log.targetType] ?? log.targetType) : '—'}
            {log.targetId ? <span className="ml-1 font-mono">{log.targetId}</span> : null}
          </span>
        ),
      },
      {
        key: 'result',
        header: '结果',
        render: (log) => (
          <Badge
            tone={log.result === 'success' ? 'ok' : log.result === 'denied' ? 'warning' : 'critical'}
          >
            {RESULT_LABELS[log.result]}
          </Badge>
        ),
      },
      {
        key: 'ip',
        header: '来源 IP',
        hideOnMobile: true,
        render: (log) => <span className="font-mono text-xs text-fg-muted">{log.ip ?? '—'}</span>,
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (log) => (
          <Button size="sm" disabled={!log.detail} onClick={() => setDetail(log)}>
            详情
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-3 p-3 lg:p-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-full sm:w-56">
          <TextInput
            label="动作"
            placeholder="如 drone.command.flight"
            value={action}
            onChange={(event) => setAction(event.target.value)}
          />
        </div>
        <div className="w-full sm:w-40">
          <Select
            label="目标类型"
            placeholder="全部"
            options={TARGET_TYPES.map((value) => ({ value, label: TARGET_LABELS[value] ?? value }))}
            value={targetType}
            onChange={(event) => setTargetType(event.target.value)}
          />
        </div>
        <div className="w-full sm:w-32">
          <Select
            label="结果"
            placeholder="全部"
            options={[
              { value: 'success', label: '成功' },
              { value: 'denied', label: '被拒绝' },
              { value: 'failure', label: '失败' },
            ]}
            value={result}
            onChange={(event) => setResult(event.target.value)}
          />
        </div>
        <div className="w-full sm:w-40">
          <TextInput
            label="起始日期"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="w-full sm:w-40">
          <TextInput
            label="结束日期"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <div className="ml-auto flex gap-2">
          <Button onClick={() => void load()} loading={loading}>
            刷新
          </Button>
          {canPrune ? (
            <Button
              variant="danger"
              loading={pruning}
              onClick={(event) => void prune(event.currentTarget)}
            >
              清理过期日志
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <Card tone="critical">
          <CardBody>
            <p className="text-sm text-fg">{error}</p>
          </CardBody>
        </Card>
      ) : null}

      <DataTable
        rows={rows}
        columns={columns}
        getRowId={(log) => log.id}
        loading={loading}
        dense
        rowTone={(log) => (log.result === 'success' ? 'default' : log.result === 'denied' ? 'warning' : 'critical')}
        emptyTitle="没有符合条件的审计记录"
        emptyDescription="调整筛选条件，或等待平台产生新的操作记录。"
      />

      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-fg-muted">
          共 {data?.total ?? 0} 条 · 第 {page} / {totalPages} 页
          {rows.length > 0 ? ` · 最早一条 ${formatRelativeTime(Date.parse(rows[0]!.at), Date.now())}` : ''}
        </span>
        <div className="flex gap-2">
          <Button size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
            上一页
          </Button>
          <Button size="sm" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>
            下一页
          </Button>
        </div>
      </div>

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title="审计详情"
        size="lg"
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setDetail(null)}>关闭</Button>
          </div>
        }
      >
        {detail ? (
          <div className="flex flex-col gap-3">
            <Card>
              <CardHeader>
                <CardTitle subtitle={detail.action}>{formatDateTime(detail.at)}</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="flex flex-col gap-1 text-xs text-fg-muted">
                  <li>操作人：{detail.actorEmail || '—'}</li>
                  <li>
                    目标：{detail.targetType ? (TARGET_LABELS[detail.targetType] ?? detail.targetType) : '—'}
                    {detail.targetId ? ` / ${detail.targetId}` : ''}
                  </li>
                  <li>结果：{RESULT_LABELS[detail.result]}</li>
                  <li>来源 IP：{detail.ip ?? '—'}</li>
                </ul>
              </CardBody>
            </Card>
            <pre className="max-h-80 overflow-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-fg">
              {JSON.stringify(detail.detail, null, 2)}
            </pre>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
